"use client";

import { useDeferredValue, useEffect, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  HINT,
  LABEL,
  TEXTAREA,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type Claim,
  type DecodedJwt,
  type DurationUnit,
  decodeJwt,
  expectedSignatureBytes,
  roughDuration,
  type TimeClaim,
  type Validity,
  validityAt,
} from "@/lib/tool-kit/latex-dev/jwt";

/** English strings. A translated content file overrides these through `ui`. */
export const JWT_DECODER_STRINGS = {
  privacyHeading: "Before you paste a token",
  privacy:
    "A token that is still valid is a password. This page sends nothing and stores nothing, but any web page you paste a live token into could in principle read it. Use a token from a test system, one that has expired, or one you have already revoked.",
  inputLabel: "JSON Web Token",
  placeholder: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0In0.…",
  hint: "Decoded in your browser. A leading “Bearer ” and line breaks are ignored.",
  loadExample: "Load the example",
  clear: "Clear",
  example:
    "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJodHRwczovL2F1dGguZXhhbXBsZS5jb20iLCJzdWIiOiIxMjM0NTY3ODkwIiwiYXVkIjoiY2l0YXRpb25lYXN5LWRlbW8iLCJuYW1lIjoiSmFuZSBFeGFtcGxlIiwiYWRtaW4iOmZhbHNlLCJyb2xlcyI6WyJyZWFkZXIiLCJlZGl0b3IiXSwiaWF0IjoxNzAwMDAwMDAwLCJuYmYiOjE3MDAwMDAwMDAsImV4cCI6NDEwMjQ0NDgwMCwianRpIjoiN2YzYzJhMTAifQ.YDWNc4SOWXLStJ2g92EYQ6JpQ83DQ-JJ2NL6TDf-88Q",
  emptyPrompt: "Paste a token, or load the example, to decode it.",
  errors: {
    empty: "There is nothing to decode.",
    segments:
      "A JSON Web Token has three parts separated by dots (or five, if it is encrypted). This has {count}.",
    characters:
      "The {segment} contains characters that are not allowed in base64url (letters, digits, - and _).",
    badLength:
      "The {segment} is not valid base64url: its length cannot be right. The token may have been cut off.",
    notUtf8: "The {segment} decodes to bytes that are not valid UTF-8 text.",
    notJson:
      "The {segment} decodes to text that is not valid JSON (line {line}, column {column}).",
    notObject: "The {segment} is JSON, but not an object.",
  },
  segments: {
    header: "header",
    payload: "payload",
    signature: "signature",
  },
  results: "Decoded token",
  notVerified:
    "Decoded, not verified. Reading a token needs no key; checking its signature does, and this tool never asks for one. Do not trust any claim below until a server has verified the signature.",
  algorithmLabel: "Algorithm",
  statusLabel: "Status by its own clock",
  statuses: {
    expired: "Expired",
    notYet: "Not valid yet",
    current: "Within its time window",
    unknown: "No time limits",
    pending: "Checking…",
  },
  lifetimeLabel: "Lifetime (iat to exp)",
  unitsOne: {
    year: "one: {count} year|other: {count} years",
    day: "one: {count} day|other: {count} days",
    hour: "one: {count} hour|other: {count} hours",
    minute: "one: {count} minute|other: {count} minutes",
    second: "one: {count} second|other: {count} seconds",
  },
  aboutPrefix: "about {text}",
  absent: "—",
  noLifetime: "—",
  headerHeading: "Header",
  payloadHeading: "Payload",
  copyHeader: "Copy header",
  copyPayload: "Copy payload",
  copied: "Copied!",
  algorithmNotes: {
    HS256: "HMAC with SHA-256. One shared secret signs and verifies.",
    HS384: "HMAC with SHA-384. One shared secret signs and verifies.",
    HS512: "HMAC with SHA-512. One shared secret signs and verifies.",
    RS256:
      "RSA PKCS#1 v1.5 with SHA-256. A private key signs; the public key verifies.",
    RS384:
      "RSA PKCS#1 v1.5 with SHA-384. A private key signs; the public key verifies.",
    RS512:
      "RSA PKCS#1 v1.5 with SHA-512. A private key signs; the public key verifies.",
    PS256:
      "RSA-PSS with SHA-256. A private key signs; the public key verifies.",
    PS384:
      "RSA-PSS with SHA-384. A private key signs; the public key verifies.",
    PS512:
      "RSA-PSS with SHA-512. A private key signs; the public key verifies.",
    ES256:
      "ECDSA with P-256 and SHA-256. A private key signs; the public key verifies.",
    ES384:
      "ECDSA with P-384 and SHA-384. A private key signs; the public key verifies.",
    ES512:
      "ECDSA with P-521 and SHA-512. A private key signs; the public key verifies.",
    ES256K: "ECDSA with secp256k1 and SHA-256.",
    EdDSA: "EdDSA with Ed25519 or Ed448.",
    none: "No signature at all. Anyone could have written this token.",
    other: "An algorithm this tool does not describe.",
  },
  claimsHeading: "Registered claims",
  colClaim: "Claim",
  colMeaning: "Meaning",
  colValue: "Value",
  claimNames: {
    iss: "Issuer: who created the token",
    sub: "Subject: who the token is about",
    aud: "Audience: who the token is for",
    exp: "Expiration time",
    nbf: "Not before: earliest valid time",
    iat: "Issued at",
    jti: "JWT ID: a unique identifier",
  },
  otherHeading: "Other claims",
  headerFieldsHeading: "Header fields",
  timesHeading: "Times",
  colSeconds: "Seconds",
  colUtc: "UTC",
  colLocal: "Your time zone",
  colRelative: "Relative to now",
  outOfRange: "outside the range of dates",
  signatureHeading: "Signature",
  signatureNote: "{bytes} bytes (base64url: {text})",
  signatureNone: "empty: the token is unsecured",
  warningsHeading: "Worth knowing",
  warnings: {
    algNone:
      "The algorithm is “none”: this token is not signed, so anyone could have written it. A server must reject it.",
    algMissing:
      "The header has no “alg” field, which every signed token needs.",
    headerUrl:
      "The header names a key (jku, x5u, jwk or x5c). A server must not fetch or trust a key supplied by the token itself.",
    critical:
      "The header has a “crit” field, listing extensions a recipient must understand to accept the token.",
    noExpiry:
      "There is no “exp” claim, so the token never expires on its own. Tokens without an expiry are worth avoiding.",
    expBeforeIat: "The expiration time is earlier than the issued-at time.",
    nbfAfterExp:
      "The not-before time is later than the expiration time, so the token is never valid.",
    milliseconds:
      "A time claim is a very large number. JWT times are in seconds; this may be milliseconds.",
    notANumber:
      "A time claim (exp, nbf or iat) is not a number, so it cannot be read as a time.",
    padding:
      "A segment ends with = padding. JWTs use base64url without padding; most libraries still accept it.",
    standardBase64:
      "A segment uses + or / from standard base64. JWTs use - and _ instead.",
    prefixRemoved: "A leading “Bearer ” was removed before decoding.",
    signatureSize:
      "The signature is not the length this algorithm produces ({expected} bytes), so it cannot be valid.",
    payloadNotJson:
      "The payload is not a JSON object. That is allowed for a JWS, but then it holds no claims.",
    encrypted:
      "This is an encrypted token (JWE) with five parts. Its payload is encrypted and cannot be read without the key; only the header is shown.",
    unsecured: "The signature is empty, so the token is unsecured.",
  },
  timeWarnings: {
    expired: "This token has expired by its own “exp” claim.",
    notYet: "This token is not valid yet by its own “nbf” claim.",
  },
  payloadText: "Payload text",
} as const;

type Strings = typeof JWT_DECODER_STRINGS;
type AlgorithmKey = keyof Strings["algorithmNotes"];
type Fmt = ReturnType<typeof useToolNumber>;

const RELATIVE_UNITS: Record<DurationUnit, Intl.RelativeTimeFormatUnit> = {
  year: "year",
  day: "day",
  hour: "hour",
  minute: "minute",
  second: "second",
};

/** The current time in milliseconds, read after mount and then every second. */
function useNow(active: boolean): number | null {
  const [now, setNow] = useState<number | null>(null);
  useEffect(() => {
    if (!active) {
      return;
    }
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return active ? now : null;
}

function utcText(seconds: number, locale: string, strings: Strings): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "long",
      timeStyle: "long",
      timeZone: "UTC",
    }).format(seconds * 1000);
  } catch {
    return strings.outOfRange;
  }
}

function localText(seconds: number, locale: string, strings: Strings): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "medium",
    }).format(seconds * 1000);
  } catch {
    return strings.outOfRange;
  }
}

function relativeText(seconds: number, nowMs: number, locale: string): string {
  const delta = seconds - nowMs / 1000;
  const { unit, count } = roughDuration(delta);
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  return formatter.format(delta < 0 ? -count : count, RELATIVE_UNITS[unit]);
}

function durationText(
  seconds: number,
  locale: string,
  strings: Strings,
  fmt: Fmt
): string {
  const { unit, count } = roughDuration(seconds);
  const exact = Math.abs(seconds) === count * unitSeconds(unit);
  const text = tPlural(strings.unitsOne[unit], locale, count, {
    count: fmt(count),
  }).trim();
  return exact ? text : t(strings.aboutPrefix, { text });
}

function unitSeconds(unit: DurationUnit): number {
  const sizes: Record<DurationUnit, number> = {
    year: 31_536_000,
    day: 86_400,
    hour: 3600,
    minute: 60,
    second: 1,
  };
  return sizes[unit];
}

function claimValue(claim: Claim): string {
  return typeof claim.value === "string" ? claim.value : claim.json;
}

export default function JwtDecoderWidget() {
  const strings = useToolUi(JWT_DECODER_STRINGS);
  const [source, setSource] = useState("");
  const deferred = useDeferredValue(source);
  const result = deferred.trim() === "" ? null : decodeJwt(deferred);
  const hasTimes = result?.ok === true && result.jwt.times.length > 0;
  const now = useNow(hasTimes);

  return (
    <section className="space-y-6">
      <Notice>
        <strong>{strings.privacyHeading}.</strong> {strings.privacy}
      </Notice>
      <TextAreaField
        hint={strings.hint}
        id="jwt-input"
        label={strings.inputLabel}
        onChange={setSource}
        placeholder={strings.placeholder}
        rows={6}
        value={source}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SMALL}
          onClick={() => setSource(strings.example)}
          type="button"
        >
          {strings.loadExample}
        </button>
        <button
          className={BUTTON_SMALL}
          onClick={() => setSource("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <div aria-live="polite" className="space-y-4">
        {result === null ? (
          <Notice>{strings.emptyPrompt}</Notice>
        ) : (
          <Results now={now} result={result} strings={strings} />
        )}
      </div>
    </section>
  );
}

function Results({
  result,
  strings,
  now,
}: {
  result: ReturnType<typeof decodeJwt>;
  strings: Strings;
  now: number | null;
}) {
  const fmt = useToolNumber();
  if (!result.ok) {
    const { error } = result;
    const segment = Object.hasOwn(strings.segments, error.segment)
      ? strings.segments[error.segment as keyof Strings["segments"]]
      : "";
    return (
      <ErrorNotice>
        {t(strings.errors[error.code], {
          count: fmt(error.count),
          segment,
          line: fmt(error.line),
          column: fmt(error.column),
        })}
      </ErrorNotice>
    );
  }
  return <Decoded fmt={fmt} jwt={result.jwt} now={now} strings={strings} />;
}

function algorithmNote(algorithm: string, strings: Strings): string {
  const key = algorithm.toLowerCase() === "none" ? "none" : algorithm;
  return Object.hasOwn(strings.algorithmNotes, key)
    ? strings.algorithmNotes[key as AlgorithmKey]
    : strings.algorithmNotes.other;
}

function Decoded({
  jwt,
  strings,
  now,
  fmt,
}: {
  jwt: DecodedJwt;
  strings: Strings;
  now: number | null;
  fmt: Fmt;
}) {
  const locale = useToolLocale();
  const validity: Validity | "pending" =
    now === null ? "pending" : validityAt(jwt.times, Math.floor(now / 1000));
  const iat = jwt.times.find((x) => x.name === "iat");
  const exp = jwt.times.find((x) => x.name === "exp");
  const lifetime =
    iat && exp
      ? durationText(exp.seconds - iat.seconds, locale, strings, fmt)
      : strings.noLifetime;
  const timeWarnings =
    validity === "expired" || validity === "notYet"
      ? [strings.timeWarnings[validity]]
      : [];
  const warnings = [
    ...timeWarnings,
    ...jwt.warnings.map((w) =>
      t(strings.warnings[w], {
        expected: fmt(expectedSignatureBytes(jwt.algorithm)),
      })
    ),
  ];
  return (
    <>
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <Notice>{strings.notVerified}</Notice>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.algorithmLabel}
          value={jwt.algorithm === "" ? strings.absent : jwt.algorithm}
        />
        <StatCard
          label={strings.statusLabel}
          value={strings.statuses[validity]}
        />
        <StatCard label={strings.lifetimeLabel} value={lifetime} />
      </StatGrid>
      <p className={HINT}>{algorithmNote(jwt.algorithm, strings)}</p>
      <JsonBlock
        copyLabel={strings.copyHeader}
        heading={strings.headerHeading}
        id="jwt-header"
        strings={strings}
        text={jwt.headerJson}
      />
      {jwt.payload ? (
        <JsonBlock
          copyLabel={strings.copyPayload}
          heading={strings.payloadHeading}
          id="jwt-payload"
          strings={strings}
          text={jwt.payloadJson}
        />
      ) : null}
      {jwt.payloadText === "" ? null : (
        <JsonBlock
          copyLabel={strings.copyPayload}
          heading={strings.payloadText}
          id="jwt-payload-text"
          strings={strings}
          text={jwt.payloadText}
        />
      )}
      <ClaimTables jwt={jwt} strings={strings} />
      <TimeTable
        fmt={fmt}
        jwt={jwt}
        locale={locale}
        now={now}
        strings={strings}
      />
      <SignatureLine fmt={fmt} jwt={jwt} strings={strings} />
      {warnings.length > 0 ? (
        <>
          <h3 className={WIDGET_H3}>{strings.warningsHeading}</h3>
          <ul className="list-disc space-y-1 pl-5 text-foreground text-sm">
            {warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </>
      ) : null}
    </>
  );
}

function JsonBlock({
  heading,
  text,
  copyLabel,
  id,
  strings,
}: {
  heading: string;
  text: string;
  copyLabel: string;
  id: string;
  strings: Strings;
}) {
  return (
    <div className="space-y-2">
      <label className={LABEL} htmlFor={id}>
        {heading}
      </label>
      <textarea
        className={TEXTAREA}
        id={id}
        readOnly
        rows={Math.min(16, text.split("\n").length + 1)}
        spellCheck={false}
        value={text}
      />
      <CopyButton
        copiedLabel={strings.copied}
        label={copyLabel}
        small
        text={text}
      />
    </div>
  );
}

function ClaimTables({ jwt, strings }: { jwt: DecodedJwt; strings: Strings }) {
  const names = strings.claimNames;
  return (
    <>
      {jwt.registered.length > 0 ? (
        <>
          <h3 className={WIDGET_H3}>{strings.claimsHeading}</h3>
          <DataTable
            head={[strings.colClaim, strings.colMeaning, strings.colValue]}
            rows={jwt.registered.map((claim) => [
              claim.name,
              names[claim.name as keyof typeof names],
              claimValue(claim),
            ])}
          />
        </>
      ) : null}
      {jwt.other.length > 0 ? (
        <>
          <h3 className={WIDGET_H3}>{strings.otherHeading}</h3>
          <DataTable
            head={[strings.colClaim, strings.colValue]}
            rows={jwt.other.map((claim) => [claim.name, claimValue(claim)])}
          />
        </>
      ) : null}
    </>
  );
}

function TimeTable({
  jwt,
  strings,
  locale,
  now,
  fmt,
}: {
  jwt: DecodedJwt;
  strings: Strings;
  locale: string;
  now: number | null;
  fmt: Fmt;
}) {
  if (jwt.times.length === 0) {
    return null;
  }
  const row = (time: TimeClaim) => [
    time.name,
    fmt(time.seconds, { useGrouping: false }),
    utcText(time.seconds, locale, strings),
    now === null ? "" : localText(time.seconds, locale, strings),
    now === null ? "" : relativeText(time.seconds, now, locale),
  ];
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.timesHeading}</h3>
      <DataTable
        head={[
          strings.colClaim,
          strings.colSeconds,
          strings.colUtc,
          strings.colLocal,
          strings.colRelative,
        ]}
        rows={jwt.times.map(row)}
      />
    </>
  );
}

function SignatureLine({
  jwt,
  strings,
  fmt,
}: {
  jwt: DecodedJwt;
  strings: Strings;
  fmt: Fmt;
}) {
  const text =
    jwt.signatureBytes === 0
      ? strings.signatureNone
      : t(strings.signatureNote, {
          bytes: fmt(jwt.signatureBytes),
          text:
            jwt.signature.length > 24
              ? `${jwt.signature.slice(0, 24)}…`
              : jwt.signature,
        });
  return (
    <>
      <h3 className={WIDGET_H3}>{strings.signatureHeading}</h3>
      <p className="text-foreground text-sm">{text}</p>
    </>
  );
}
